import React, { createContext, useContext, useState, useCallback } from 'react';

// ── Tipos ────────────────────────────────────────────────────────────────────

export interface AppUser {
  id: number;
  nombre: string;
  nickname: string;
  /** Rol en el sistema. Las sesiones anteriores a este cambio no lo traen. */
  rol?: 'admin' | 'asistente';
}

interface UserContextValue {
  user: AppUser | null;
  setUser: (u: AppUser | null) => void;
  logout: () => void;
}

// ── Contexto ─────────────────────────────────────────────────────────────────

const UserContext = createContext<UserContextValue>({
  user: null,
  setUser: () => {
    ""
  },
  logout: () => {
    ""
  },
});

// ── Provider ─────────────────────────────────────────────────────────────────

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUserState] = useState<AppUser | null>(() => {
    try {
      const raw = localStorage.getItem('user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const setUser = useCallback((u: AppUser | null) => {
    setUserState(u);
    if (u) {
      localStorage.setItem('user', JSON.stringify(u));
    } else {
      localStorage.removeItem('user');
    }
  }, []);

  const logout = useCallback(() => {
    setUserState(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('loginTime');
  }, []);

  return (
    <UserContext.Provider value={{ user, setUser, logout }}>
      {children}
    </UserContext.Provider>
  );
};

// ── Hook ─────────────────────────────────────────────────────────────────────

export const useUser = () => useContext(UserContext);
